import React, { SyntheticEvent, useCallback, useState, useEffect } from 'react';
import classNames from 'classnames';
import { Button, Checkbox, Stack, Text, ModalOverlay, Overlay } from '@moda/om';
import { useHistory } from 'react-router-dom';
import { useUser } from '../../hooks/useUser';
import { hrefFor } from '../../routers';
import { useEnrollMutation } from '../../hooks/useEnroll';
import { LoadingBalls } from '../../components/LoadingBalls';
import { PageMeta } from '../../components/PageMeta';
import { UserRewardStatus, UserStatusLevel } from '../../constants';
import { useAuthModal } from '../../hooks/useAuthModal';

import './RewardsPage.scss';

export const RewardsPage: React.FC = () => {
  const { user } = useUser();
  const history = useHistory();
  const { handleEnroll, loading } = useEnrollMutation();

  const { openAuthModal, show: showAuth, closeAuthModal } = useAuthModal();

  const [disabled, setDisabled] = useState(true);
  const [redTerms, setRedTerms] = useState(false);
  const [checked, setChecked] = useState(false);
  const [loader, setLoader] = useState(false);

  const noAccess =
    user.statusLevel === UserStatusLevel.Employee ||
    user.userLoyaltyStatus === UserRewardStatus.NoAccess;

  useEffect(() => {
    if (user.isLoggedIn && user.userLoyaltyStatus === UserRewardStatus.Enrolled) {
      history.push(hrefFor.AccountRewards());
    }
  }, [history, user.isLoggedIn, user.userLoyaltyStatus]);

  const authCallback = useCallback(() => {
    setLoader(true);
    handleEnroll();
  }, [handleEnroll]);

  const onClickJoin = useCallback(
    (event: SyntheticEvent) => {
      event.preventDefault();
      user.isLoggedIn ? handleEnroll() : openAuthModal(authCallback);
    },
    [user.isLoggedIn, handleEnroll, openAuthModal, authCallback]
  );

  const onCloseModal = useCallback(() => {
    closeAuthModal();
    setRedTerms(false);
  }, [closeAuthModal]);

  const handleChange = useCallback(() => {
    if (disabled) {
      setDisabled(false);
      setRedTerms(false);
      setChecked(true);
    } else {
      setDisabled(true);
      setRedTerms(true);
      setChecked(false);
    }
  }, [disabled]);

  return (
    <>
      <PageMeta
        title="Moda Rewards"
        description="Rewarding Exceptional Taste. Members of Moda Rewards earn purchase rewards points and other exclusive perks. Get 500 bonus points when you sign up."
      />
      <div className="RewardsPage">
        <div className="RewardsPage__wrapper">
          <Stack className="RewardsPage__section-1" space={6}>
            <Text treatment="eyebrow">MODA REWARDS</Text>
            <Stack space={3}>
              <Text className="RewardsPage__title-primary" treatment="display" family="serif">
                Rewarding
              </Text>
              <Text className="RewardsPage__title-secondary" treatment="display">
                Exceptional Taste
              </Text>
            </Stack>
            <Text treatment="h4" family="serif">
              Members of Moda Rewards earn purchase rewards points and other exclusive perks. Get
              500 bonus points when you sign up.
            </Text>
            <div
              className={classNames('RewardsPage__terms', {
                'RewardsPage__terms--error': redTerms
              })}
            >
              To start earning Moda Rewards points, please agree to our{' '}
              <a
                className={classNames('RewardsPage__terms-link', {
                  'RewardsPage__terms-link--error': redTerms
                })}
                href="/terms"
                rel="noopener noreferrer"
                target="_blank"
              >
                Terms &amp; Conditions
              </a>
              .
            </div>
            <form onSubmit={onClickJoin}>
              <Checkbox
                className="RewardsPage__checkbox"
                id="rewards"
                type="checkbox"
                checked={checked}
                onChange={handleChange}
              >
                YES, I AGREE TO TERMS &amp; CONDITIONS
              </Checkbox>
              <Button
                className="RewardsPage__join-button"
                type="submit"
                disabled={disabled || noAccess}
              >
                JOIN NOW
              </Button>
              {noAccess && (
                <div>
                  <br />
                  <Text treatment="bold1">
                    <span className="RewardsPage__access">
                      We are sorry but you do not have access to rewards.
                    </span>
                  </Text>
                </div>
              )}
            </form>

            <Overlay show={loading || loader}>
              <LoadingBalls />
              <Text treatment="eyebrow">Joining Rewards</Text>
            </Overlay>
          </Stack>
        </div>

        <Stack className="RewardsPage__section-2" space={6}>
          <Text treatment="h3" family="serif">
            HOW IT WORKS
          </Text>
          <div className="RewardsPage__details">
            <Stack className="RewardsPage__column" space={2}>
              <Text treatment="h4" family="serif">
                1.
              </Text>
              <Text treatment="h4" family="serif">
                Join the Program
              </Text>
              <Text treatment="body1" family="serif">
                Signing up is free &amp; easy. Just sign-in to your Moda account (or create one),
                and enroll here. You'll get 500 bonus points just for joining.
              </Text>
            </Stack>
            <Stack className="RewardsPage__column" space={2}>
              <Text treatment="h4" family="serif">
                2.
              </Text>
              <Text treatment="h4" family="serif">
                Shop To Earn
              </Text>
              <Text treatment="body1" family="serif">
                Once you're enrolled, most purchases you make will automatically add Rewards points
                to your account. Visit your Account Center to track your redeemable points.
              </Text>
            </Stack>
            <Stack className="RewardsPage__column" space={2}>
              <Text treatment="h4" family="serif">
                3.
              </Text>
              <Text treatment="h4" family="serif">
                Redeem York Rewards
              </Text>
              <Text treatment="body1" family="serif">
                For every 1,000 points you earn, a $20 Reward will be added to your account. You can
                redeem Rewards during Checkout on any order.
              </Text>
            </Stack>
          </div>
          <Text className="RewardsPage__section-footer" treatment="body2" color="fog">
            *Please enroll in Moda Rewards with your preferred currency selected. Rewards can only
            be redeemed using the currency in which they were earned. See{' '}
            <a
              className="RewardsPage__link"
              href="http://help.modaoperandi.com/hc/en-us"
              rel="noopener noreferrer"
              target="_blank"
            >
              FAQs
            </a>{' '}
            for more information.
          </Text>
        </Stack>

        <Stack className="RewardsPage__section-3" space={6}>
          <Text treatment="h3" family="serif">
            WHY JOIN MODA REWARDS
          </Text>
          <div className="RewardsPage__details">
            <Stack className="RewardsPage__column" space={2}>
              <Text treatment="h4" family="serif">
                Points On Purchases
              </Text>
              <Text treatment="body1" family="serif">
                You'll earn 1 point for every dollar spent, and receive $20 in Moda Rewards every
                time you reach 1,000 points.
              </Text>
            </Stack>
            <Stack className="RewardsPage__column" space={2}>
              <Text treatment="h4" family="serif">
                Advance Access
              </Text>
              <Text treatment="body1" family="serif">
                Get a first look at new arrivals and receive special offers ahead of anyone else.
              </Text>
            </Stack>
            <Stack className="RewardsPage__column" space={2}>
              <Text treatment="h4" family="serif">
                Insider Updates
              </Text>
              <Text treatment="body1" family="serif">
                Never miss a moment with exclusive updates and special surprises delivered
                throughout the year.
              </Text>
            </Stack>
          </div>
        </Stack>
      </div>

      {user.isLoggedIn && showAuth && (
        <ModalOverlay onClose={onCloseModal} className="RewardsPage__modal">
          <Stack space={6}>
            <Text treatment="h3" family="serif">
              You have already enrolled in Moda Rewards.
            </Text>
            <Text treatment="body2">
              Please visit your Account Center to track Moda Rewards activity.
            </Text>
            <Button to={hrefFor.AccountRewards()}>Account Center</Button>
          </Stack>
        </ModalOverlay>
      )}
    </>
  );
};
